import React, { useEffect, useState } from "react";
import { Pages, Packages as PackagesApi } from "@/lib/api";
import { Link } from "react-router-dom";
import WaterHero from "@/components/WaterHero";
import Footer from "@/components/Footer";
import EditableHTML from "@/components/EditableHTML";
import SporeBullet from "@/components/SporeBullet";
import QuoteForm from "@/components/QuoteForm";
import TestimonialsSection from "@/components/TestimonialsSection";
import GallerySection from "@/components/GallerySection";
import SEO from "@/components/SEO";
import ReflectedHeading from "@/components/ReflectedHeading";
import { ArrowUpRight, Shield, Droplets, Wind } from "lucide-react";

const stripPara = (html) => (html || "").replace(/<\/?p[^>]*>/g, "").trim();

const SERVICE_ICONS = [Shield, Droplets, Wind];

const SERVICE_IMAGES = [
  "https://images.unsplash.com/photo-1762281532443-3206b6116ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJyaWdodCUyMGdyZWVuJTIwYmx1ZSUyMG9yZ2FuaWMlMjBwYXR0ZXJufGVufDB8fHx8MTc3ODY3NjY4NHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1615657220350-ad1f77adc232?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHx3YXRlciUyMGRhbWFnZSUyMHJlc3RvcmF0aW9uJTIwd29ya2VyfGVufDB8fHx8MTc3ODY3NjY1Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1695445338202-99ee356462eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwYWlyJTIwc2NydWJiZXIlMjBkZWh1bWlkaWZpZXJ8ZW58MHx8fHwxNzc4Njc2NjU1fDA&ixlib=rb-4.1.0&q=85",
];

const Home = () => {
  const [content, setContent] = useState({});
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const page = await Pages.get("home");
        setContent(page.sections || {});
      } catch {}
      try {
        const list = await PackagesApi.list(true);
        setPackages(list.slice(0, 3));
      } catch {}
    })();
  }, []);

  const scrollTo = (id) => () => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };
  const goContact = () => { window.location.href = "/contact"; };

  return (
    <main className="min-h-screen text-slate-100 relative">
      <SEO
        title="Thermification™ Restoration Equipment"
        description="ATC Mold & Water — professional Thermification™ equipment for mold remediation, water damage restoration, and disaster-response drying. Chemical-free, ozone-free indirect-fired dry heat platform."
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is Thermification™?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Thermification™ is ATC Mold & Water's indirect-fired dry-heat drying platform for restoration jobsites. It combines high-volume heated dry air, air-exchange, and HEPA filtration to remove moisture from structures faster than conventional dehumidification — without chemicals or ozone.",
              },
            },
            {
              "@type": "Question",
              name: "Is Thermification™ chemical-free?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Thermification™ is 100% chemical-free and ozone-free. It uses indirect-fired dry heat and mechanical air-exchange only, making it safe for occupied structures, sensitive contents, and documentation-ready insurance workflows.",
              },
            },
            {
              "@type": "Question",
              name: "What types of jobs is Thermification™ used on?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Thermification™ equipment is used by restoration pros for mold remediation, water damage restoration, flood cleanup, storm damage response, fire and smoke damage, sewage cleanup, and structural drying on both residential and commercial jobsites.",
              },
            },
            {
              "@type": "Question",
              name: "Which Thermification™ package should I choose?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ATC offers six Thermification™ configurations — Core, Elite, Plus, Ultra, Pro, and Master. Package selection depends on the size and volume of jobs you handle, power availability on site, and whether you need containment and air-scrubbing gear included. Contact ATC for a package-fit consultation.",
              },
            },
            {
              "@type": "Question",
              name: "How do I request a quote for equipment?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Browse the equipment catalog at atcmoldandwater.com/equipment, open any package for details, and click Request a Quote — or email media@atcpestcontrol.com directly.",
              },
            },
          ],
        }}
      />
      <WaterHero
        content={content}
        onPrimary={goContact}
        onSecondary={scrollTo("#packages")}
        onTertiary={goContact}
      />

      {/* About */}
      <section className="water-section max-w-7xl mx-auto px-6 lg:px-10 py-28" data-testid="about-section">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-5">
              <EditableHTML html={content.about_eyebrow} as="span" />
            </div>
            <ReflectedHeading
              as="h2"
              className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight thermification-gradient-text"
            >
              <EditableHTML html={content.about_title} as="span" />
            </ReflectedHeading>
            {content.about_image_url && (
              <div className="mt-10 relative" data-testid="about-image">
                <div className="absolute inset-0 blur-3xl opacity-40 pointer-events-none"
                  style={{ background: "radial-gradient(circle at center, rgba(30,144,255,0.6), transparent 65%)" }}
                  aria-hidden="true" />
                <img
                  src={content.about_image_url}
                  alt=""
                  className="relative max-h-[360px] w-auto mx-auto lg:mx-0 object-contain animate-fade-up"
                  style={{
                    filter: "drop-shadow(0 20px 40px rgba(30,144,255,0.35))",
                    mixBlendMode: "multiply",
                  }}
                />
              </div>
            )}
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/5">
            <EditableHTML
              html={content.about_body}
              className="text-slate-300 text-base sm:text-lg leading-relaxed space-y-4"
            />
            <Link
              to="/p/thermification-explained"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full brand-gradient text-white text-sm font-semibold hover:opacity-95 transition shadow-[0_10px_40px_-10px_rgba(30,144,255,0.55)]"
              data-testid="about-thermification-cta"
            >
              Read the full deep-dive
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>

      {/* Packages preview */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>
      <section id="packages" className="water-section py-28" data-testid="packages-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-5">
                <EditableHTML html={content.packages_eyebrow} as="span" />
              </div>
              <ReflectedHeading
                as="h2"
                className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-white max-w-2xl"
              >
                <EditableHTML html={content.packages_title} as="span" />
              </ReflectedHeading>
              <EditableHTML
                html={content.packages_subtitle}
                className="text-slate-400 mt-6 max-w-xl"
              />
            </div>
            <Link
              to="/equipment"
              className="inline-flex items-center gap-2 text-sm text-atc-blue hover:text-atc-green transition group"
              data-testid="packages-view-all"
            >
              View all equipment <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((p, idx) => (
              <Link
                key={p.id}
                to={`/equipment/${p.slug}`}
                className="glass glass-hover rounded-2xl overflow-hidden group"
                data-testid={`package-card-${p.slug}`}
              >
                <div className="aspect-[4/3] bg-contain bg-center bg-no-repeat bg-slate-950/60 border-b border-white/5" style={{ backgroundImage: `url(${p.image_url})` }} />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="font-display font-bold text-lg text-white">{p.name}</h3>
                    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-atc-blue transition" />
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">{p.short_description}</p>
                  <div className="mt-5 inline-flex items-center text-xs uppercase tracking-[0.18em] text-atc-green">
                    {p.price || "Request a Quote"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>
      <GallerySection content={content} />

      {/* Testimonials */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>
      <TestimonialsSection content={content} />

      {/* Contact / Quote */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="waterline" /></div>
      <section id="contact" className="water-section py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-5">
              <EditableHTML html={content.contact_eyebrow} as="span" />
            </div>
            <ReflectedHeading
              as="h2"
              className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-white"
            >
              <EditableHTML html={content.contact_title} as="span" />
            </ReflectedHeading>
            <div className="space-y-4 text-slate-300 mt-10">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Phone</div>
                <EditableHTML html={content.contact_phone} className="text-lg" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Email</div>
                <EditableHTML html={content.contact_email} className="text-lg" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">Service Area</div>
                <EditableHTML html={content.contact_address} className="text-sm text-slate-400" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer content={content} />
    </main>
  );
};

export default Home;
